/**
 * How to use the transcription APIs — copy-paste Python and C# examples for third-party
 * integrators, a field-by-field payload reference, plus the correlation-id conventions used
 * for support.
 */

import { useMemo, useState } from 'react';
import {
  Tab,
  Tabs,
} from '@mui/material';
import {
  Button,
  Chip,
  ShellHeader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@coordio/ui';
import { PageContainer } from '@coordio/ui/layout';
import { Copy } from 'lucide-react';
import { getEffectiveApiUrl } from '../config/runtimeEnv';
import { useNotificationStore } from '../store/notificationStore';

type LanguageKey = 'python' | 'csharp' | 'curl';
type PayloadTab = 'request' | 'response' | 'hub';

type PayloadField = {
  name: string;
  need: 'Required' | 'Optional' | 'When complete' | 'When failed' | 'Agent jobs' | 'Browser' | 'Push-stream';
  meaning: string;
};

const CodeBlock = ({ code, onCopy }: { code: string; onCopy: (code: string) => void }) => (
  <div className="relative">
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 z-10"
            onClick={() => onCopy(code)}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Copy</TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <pre className="m-0 overflow-auto rounded border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-2 font-mono text-[13px] leading-[1.6]">
      {code}
    </pre>
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-3 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-3">
    <h6 className="mb-2 text-base font-semibold">{title}</h6>
    {children}
  </div>
);

const needChipColor = (need: PayloadField['need']): 'blue' | 'green' | 'amber' | 'default' => {
  if (need === 'Required') return 'blue';
  if (need === 'When complete') return 'green';
  if (need === 'When failed') return 'amber';
  return 'default';
};

const FieldTable = ({
  fields,
  fieldLabel = 'Field',
}: {
  fields: PayloadField[];
  fieldLabel?: string;
}) => (
  <div className="mb-2 overflow-auto rounded border border-[var(--color-border-default)]">
    <table className="w-full text-sm" aria-label={`${fieldLabel} reference`}>
      <thead>
        <tr className="border-b border-[var(--color-border-default)] bg-[var(--color-bg-subtle)]">
          <th className="px-3 py-2 text-left font-medium">{fieldLabel}</th>
          <th className="w-[140px] px-3 py-2 text-left font-medium">Need</th>
          <th className="px-3 py-2 text-left font-medium">What it means</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field.name} className="border-b border-[var(--color-border-default)] last:border-b-0">
            <td className="whitespace-nowrap px-3 py-2.5 align-top font-mono text-[13px]">
              {field.name}
            </td>
            <td className="px-3 py-2.5 align-top">
              <Chip
                label={field.need}
                className="text-xs"
                color={needChipColor(field.need)}
                variant={field.need === 'Required' ? 'filled' : 'outlined'}
              />
            </td>
            <td className="px-3 py-2.5 align-top">{field.meaning}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const REQUEST_HEADERS: PayloadField[] = [
  {
    name: 'Authorization',
    need: 'Required',
    meaning: 'Bearer <your project API key>. You may send X-Api-Key: <key> instead — both identify the tenant and project.',
  },
  {
    name: 'Content-Type',
    need: 'Required',
    meaning: 'Must be multipart/form-data when creating a job. Do not send application/json for the upload.',
  },
  {
    name: 'X-Correlation-Id',
    need: 'Optional',
    meaning: 'Your own trace value (order number, ticket id). It is stored as client_correlation_id and echoed so support can find the job from either side.',
  },
];

const REQUEST_FIELDS: PayloadField[] = [
  {
    name: 'file',
    need: 'Required',
    meaning: 'The audio part. WAV, MP3, M4A, WEBM, and other formats Azure Speech Fast Transcription accepts. Keep each file under about 2 hours and 250 MB.',
  },
  {
    name: 'mode',
    need: 'Optional',
    meaning: 'How to process the file. batch (default) waits for the full transcript. batch_recording is the same flow for a clip you just recorded. realtime_file transcribes the upload and streams phrases over the hub as they finalize.',
  },
  {
    name: 'language',
    need: 'Optional',
    meaning: 'Recognition locale such as en-US, en-GB, or hi-IN. Defaults to en-US. On an agent-scoped job this overrides the agent Speech language for this upload only.',
  },
  {
    name: 'diarization',
    need: 'Optional',
    meaning: 'Speaker labels in the transcript (Speaker 1:, Speaker 2:). Defaults to true. Send false to turn it off. On an agent-scoped job this overrides the agent Speech setting.',
  },
  {
    name: 'vocabulary',
    need: 'Optional',
    meaning: 'Comma-separated names and terms to bias recognition (Contoso,Jessie,Rehaan). On an agent-scoped job this replaces the agent phrase list for this upload only.',
  },
  {
    name: 'timestamps',
    need: 'Optional',
    meaning: 'Stored with the job. The HTTP body always returns the transcript in text — there is no separate timestamp array in the JSON today.',
  },
  {
    name: 'response_format',
    need: 'Optional',
    meaning: 'Stored with the job (json by default). Create and poll still return the job object below, not a raw text body.',
  },
];

const RESPONSE_FIELDS: PayloadField[] = [
  {
    name: 'id',
    need: 'Required',
    meaning: 'The job identifier. Poll GET …/transcriptions/{id} with this value.',
  },
  {
    name: 'correlation_id',
    need: 'Required',
    meaning: 'Our trace id for the job (also returned on the X-Correlation-Id response header). Quote this to support. Use it to list every job under the same correlation.',
  },
  {
    name: 'client_correlation_id',
    need: 'Optional',
    meaning: 'The X-Correlation-Id value you sent, if any. Omitted when you did not send the header.',
  },
  {
    name: 'mode',
    need: 'Required',
    meaning: 'How audio was supplied, as stored: BatchFile, BatchLiveRecording, RealtimeFile, or RealtimeMic. This is not the same spelling as the form field (batch vs BatchFile).',
  },
  {
    name: 'status',
    need: 'Required',
    meaning: 'Lifecycle: Pending (queued), Processing (recognizing), Completed (read text), Failed (read error_code), Cancelled (stopped early). Keep polling while Pending or Processing.',
  },
  {
    name: 'language',
    need: 'Required',
    meaning: 'Locale actually used for recognition.',
  },
  {
    name: 'file_name',
    need: 'Optional',
    meaning: 'Original file name from the upload.',
  },
  {
    name: 'audio_size_bytes',
    need: 'Optional',
    meaning: 'Size of the uploaded file in bytes.',
  },
  {
    name: 'text',
    need: 'When complete',
    meaning: 'Raw speech-to-text. With diarization on, lines look like Speaker 1: … . Empty or omitted until the job finishes.',
  },
  {
    name: 'corrected_text',
    need: 'Agent jobs',
    meaning: 'Present only on agent-scoped jobs when prompt correction succeeded. text stays the raw transcript. Omitted or null if correction was skipped or failed — the job is still Completed.',
  },
  {
    name: 'correction_session_id',
    need: 'Agent jobs',
    meaning: 'Agent session created for the correction pass. Open it under /sessions/{id} in the app to review the correction conversation. Omitted when no agent was attached or the session could not be created.',
  },
  {
    name: 'agent_slug',
    need: 'Agent jobs',
    meaning: 'Slug of the agent this job was scoped to. Omitted on the unscoped /v1/audio/transcriptions API.',
  },
  {
    name: 'duration_seconds',
    need: 'When complete',
    meaning: 'Audio length in seconds when the recognizer reported it.',
  },
  {
    name: 'provider',
    need: 'Required',
    meaning: 'Speech engine used. Currently AzureSpeech.',
  },
  {
    name: 'error_code',
    need: 'When failed',
    meaning: 'Stable code such as stt_failed, missing_audio, or speech_start_failed. Omitted on success.',
  },
  {
    name: 'error_message',
    need: 'When failed',
    meaning: 'Human-readable failure detail. Omitted on success.',
  },
  {
    name: 'http_request_id',
    need: 'Optional',
    meaning: 'Server trace id from the accepting HTTP request (or the SignalR connection id for live mic). Useful when matching host logs.',
  },
  {
    name: 'started_at',
    need: 'Optional',
    meaning: 'When recognition started (UTC).',
  },
  {
    name: 'completed_at',
    need: 'When complete',
    meaning: 'When the job reached Completed, Failed, or Cancelled (UTC).',
  },
  {
    name: 'created_at',
    need: 'Required',
    meaning: 'When the job was accepted (UTC).',
  },
  {
    name: 'options_json',
    need: 'Optional',
    meaning: 'The recognition options that were stored with the job (language, diarization, vocabulary, and so on).',
  },
];

const HUB_METHODS: PayloadField[] = [
  {
    name: 'StartMicSession',
    need: 'Push-stream',
    meaning: 'Arguments: language, clientCorrelationId, tenantId, projectId, agentSlug. With an API key, pass null for tenantId and projectId. Returns { transactionId, correlationId }. The server recognizes the audio you send with SendAudio.',
  },
  {
    name: 'SendAudio',
    need: 'Push-stream',
    meaning: 'One argument: the next chunk of 16 kHz mono PCM16 bytes. Call this repeatedly while the microphone is open.',
  },
  {
    name: 'StartBrowserMicSession',
    need: 'Browser',
    meaning: 'Same arguments as StartMicSession, but recognition runs in your app with the Azure Speech JS SDK (get a token from POST /v1/audio/speech-token). Returns { transactionId, correlationId, language, diarization, phrases } so you can configure the recognizer; report results with ReportFinal instead of sending audio.',
  },
  {
    name: 'ReportFinal',
    need: 'Browser',
    meaning: 'Arguments: text, speakerId. Sends one finalized utterance recognized in your app; the server accumulates, checkpoints, and persists it. Only valid for sessions started with StartBrowserMicSession.',
  },
  {
    name: 'ResumeBrowserMicSession',
    need: 'Browser',
    meaning: 'Arguments: transactionId, tenantId, projectId. Rebinds a browser session after your connection reconnected (the server parks it for ~2 minutes on a drop). Call this from your onreconnected handler so ReportFinal and StopMicSession keep working; recognition in your app never stopped.',
  },
  {
    name: 'StopMicSession',
    need: 'Required',
    meaning: 'Ends recognition. Optional arguments: transactionId, tenantId, projectId — pass them so Stop still resolves the session after a reconnect or server restart (idempotent: an already-finished transaction returns its status instead of an error). Returns { transactionId, correlationId, status } only — long transcripts exceed SignalR message limits, so fetch the persisted transcript with GET /transcriptions/{id}. With an agent slug, correction runs in the background; poll the same GET until corrected_text appears (a correction_failed event means the raw transcript is final).',
  },
  {
    name: 'CancelMicSession',
    need: 'Optional',
    meaning: 'Stops early and keeps whatever was recognized so far (status Cancelled).',
  },
  {
    name: 'WatchTransaction',
    need: 'Optional',
    meaning: 'Arguments: transactionId, tenantId, projectId. Subscribe to progress for a realtime_file REST job. With an API key, tenantId and projectId may be null.',
  },
];

const HUB_EVENTS: PayloadField[] = [
  {
    name: 'PartialTranscript',
    need: 'Optional',
    meaning: '{ transactionId, text, speakerId } — in-progress hypothesis. Replace the last partial; do not append it.',
  },
  {
    name: 'FinalTranscript',
    need: 'Optional',
    meaning: '{ transactionId, text, speakerId } — a committed phrase. Append this to the transcript you show the user.',
  },
  {
    name: 'Status',
    need: 'Optional',
    meaning: '{ transactionId, status, message } — Processing, Completed, or Cancelled.',
  },
  {
    name: 'Error',
    need: 'When failed',
    meaning: '{ transactionId, errorCode, errorMessage } — recognition failed for this session or watched job.',
  },
];

const ACCEPTED_EXAMPLE = `{
  "id": "3f2a9c1e-4b70-4d2a-9c8f-1a2b3c4d5e6f",
  "correlation_id": "9c1e4b70-2a9c-4d2a-8f1a-2b3c4d5e6f70",
  "client_correlation_id": "order-4711",
  "mode": "BatchFile",
  "status": "Pending",
  "language": "en-US",
  "file_name": "meeting.mp3",
  "audio_size_bytes": 184320,
  "provider": "AzureSpeech",
  "http_request_id": "0HMVK2Q1ABCDE",
  "created_at": "2026-08-13T20:00:00Z"
}`;

const COMPLETED_EXAMPLE = `{
  "id": "3f2a9c1e-4b70-4d2a-9c8f-1a2b3c4d5e6f",
  "correlation_id": "9c1e4b70-2a9c-4d2a-8f1a-2b3c4d5e6f70",
  "client_correlation_id": "order-4711",
  "mode": "BatchFile",
  "status": "Completed",
  "language": "en-US",
  "file_name": "meeting.mp3",
  "audio_size_bytes": 184320,
  "text": "Speaker 1: Hello, this is Contoso calling about order 4711.",
  "agent_slug": "medical-corrector",
  "corrected_text": "Speaker 1: Hello, this is Contoso calling about order 4711.",
  "duration_seconds": 12.4,
  "provider": "AzureSpeech",
  "started_at": "2026-08-13T20:00:01Z",
  "completed_at": "2026-08-13T20:00:08Z",
  "created_at": "2026-08-13T20:00:00Z"
}`;

const LIST_EXAMPLE = `{
  "data": [
    { "id": "3f2a9c1e-4b70-4d2a-9c8f-1a2b3c4d5e6f", "status": "Completed", "text": "…" }
  ],
  "correlation_id": "9c1e4b70-2a9c-4d2a-8f1a-2b3c4d5e6f70"
}`;

const ERROR_EXAMPLE = `{
  "message": "An audio 'file' part is required."
}`;

const REQUEST_SHAPE = `# POST /v1/audio/transcriptions
# Content-Type: multipart/form-data  (not JSON)

Authorization: Bearer <your project API key>
X-Correlation-Id: order-4711          # optional

file=@meeting.mp3                     # required
mode=batch                            # optional: batch | batch_recording | realtime_file
language=en-US                        # optional, default en-US
diarization=true                      # optional, default true
vocabulary=Contoso,Jessie,Rehaan      # optional`;

export const TranscriptionHowToUsePage = () => {
  const { addNotification } = useNotificationStore();
  const [language, setLanguage] = useState<LanguageKey>('python');
  const [payloadTab, setPayloadTab] = useState<PayloadTab>('request');

  const baseUrl = useMemo(() => {
    const apiUrl = getEffectiveApiUrl();
    if (apiUrl.startsWith('http')) return apiUrl.replace(/\/api\/?$/, '');
    return typeof window !== 'undefined' ? window.location.origin : 'https://your-agentops-host';
  }, []);

  const copy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    addNotification('Example copied to clipboard', 'success');
  };

  const batchExamples: Record<LanguageKey, string> = {
    python: `import time
import requests

BASE_URL = "${baseUrl}"
API_KEY = "<your project API key>"

headers = {
    "Authorization": f"Bearer {API_KEY}",
    # Optional: your own trace value, echoed back on every record for support.
    "X-Correlation-Id": "order-4711",
}

with open("meeting.mp3", "rb") as audio:
    response = requests.post(
        f"{BASE_URL}/v1/audio/transcriptions",
        headers=headers,
        files={"file": ("meeting.mp3", audio, "audio/mpeg")},
        data={"mode": "batch", "language": "en-US"},
    )
response.raise_for_status()
job = response.json()

print("transaction:", job["id"], "correlation:", job["correlation_id"])

# Poll until the transcript is ready.
while job["status"] in ("Pending", "Processing"):
    time.sleep(2)
    job = requests.get(
        f"{BASE_URL}/v1/audio/transcriptions/{job['id']}",
        headers={"Authorization": f"Bearer {API_KEY}"},
    ).json()

if job["status"] == "Completed":
    print(job["text"])
else:
    print("failed:", job["error_code"], job["error_message"])`,

    csharp: `using System.Net.Http.Headers;
using System.Text.Json;

const string BaseUrl = "${baseUrl}";
const string ApiKey = "<your project API key>";

using var http = new HttpClient { BaseAddress = new Uri(BaseUrl) };
http.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", ApiKey);
// Optional: your own trace value, echoed back on every record for support.
http.DefaultRequestHeaders.Add("X-Correlation-Id", "order-4711");

using var form = new MultipartFormDataContent();
await using var audio = File.OpenRead("meeting.mp3");
var fileContent = new StreamContent(audio);
fileContent.Headers.ContentType = new MediaTypeHeaderValue("audio/mpeg");
form.Add(fileContent, "file", "meeting.mp3");
form.Add(new StringContent("batch"), "mode");
form.Add(new StringContent("en-US"), "language");

var createResponse = await http.PostAsync("/v1/audio/transcriptions", form);
createResponse.EnsureSuccessStatusCode();

var job = JsonSerializer.Deserialize<JsonElement>(await createResponse.Content.ReadAsStringAsync());
var transactionId = job.GetProperty("id").GetString();
Console.WriteLine($"transaction: {transactionId} correlation: {job.GetProperty("correlation_id").GetString()}");

// Poll until the transcript is ready.
string status;
do
{
    await Task.Delay(TimeSpan.FromSeconds(2));
    var pollJson = await http.GetStringAsync($"/v1/audio/transcriptions/{transactionId}");
    job = JsonSerializer.Deserialize<JsonElement>(pollJson);
    status = job.GetProperty("status").GetString()!;
}
while (status is "Pending" or "Processing");

Console.WriteLine(status == "Completed"
    ? job.GetProperty("text").GetString()
    : $"failed: {job.GetProperty("error_code").GetString()}");`,

    curl: `# Create the job
curl -X POST "${baseUrl}/v1/audio/transcriptions" \\
  -H "Authorization: Bearer <your project API key>" \\
  -H "X-Correlation-Id: order-4711" \\
  -F "file=@meeting.mp3" \\
  -F "mode=batch" \\
  -F "language=en-US"

# Poll for the transcript
curl "${baseUrl}/v1/audio/transcriptions/<transaction id>" \\
  -H "Authorization: Bearer <your project API key>"`,
  };

  const realtimeExamples: Record<LanguageKey, string> = {
    python: `# Live microphone transcription uses SignalR. Install: pip install signalrcore sounddevice numpy
import numpy as np
import sounddevice as sd
from signalrcore.hub_connection_builder import HubConnectionBuilder

BASE_URL = "${baseUrl}"
API_KEY = "<your project API key>"

connection = (
    HubConnectionBuilder()
    .with_url(f"{BASE_URL}/hubs/transcription", options={"access_token_factory": lambda: API_KEY})
    .with_automatic_reconnect({"type": "raw", "keep_alive_interval": 10, "reconnect_interval": 5})
    .build()
)

connection.on("PartialTranscript", lambda args: print("...", args[0]["text"], end="\\r"))
connection.on("FinalTranscript", lambda args: print("   ", args[0]["text"]))
connection.start()

# Tenant/project arguments may be None: your API key is tenant-scoped and the server
# resolves them from the key's claims. The optional last argument is an agent slug —
# it applies that agent's Speech config and runs prompt correction when you stop.
session = connection.send("StartMicSession", ["en-US", "order-4711", None, None, "<agent-slug>"])

# The hub expects 16 kHz mono PCM16 chunks.
def on_audio(indata, frames, time_info, status):
    connection.send("SendAudio", [indata.astype(np.int16).tobytes()])

with sd.InputStream(samplerate=16000, channels=1, dtype="int16", callback=on_audio):
    input("Listening — press Enter to stop...\\n")

connection.send("StopMicSession", [])
connection.stop()`,

    csharp: `// Live microphone transcription. Install: Microsoft.AspNetCore.SignalR.Client
using Microsoft.AspNetCore.SignalR.Client;

const string BaseUrl = "${baseUrl}";
const string ApiKey = "<your project API key>";

var connection = new HubConnectionBuilder()
    .WithUrl($"{BaseUrl}/hubs/transcription", options =>
    {
        options.AccessTokenProvider = () => Task.FromResult<string?>(ApiKey);
    })
    .WithAutomaticReconnect()
    .Build();

connection.On<JsonElement>("PartialTranscript", e => Console.Write($"\\r... {e.GetProperty("text")}"));
connection.On<JsonElement>("FinalTranscript", e => Console.WriteLine($"    {e.GetProperty("text")}"));

await connection.StartAsync();

// Tenant/project arguments may be null: your API key is tenant-scoped and the server
// resolves them from the key's claims. The optional last argument is an agent slug —
// it applies that agent's Speech config and runs prompt correction when you stop.
var session = await connection.InvokeAsync<JsonElement>("StartMicSession", "en-US", "order-4711", null, null, "<agent-slug>");
Console.WriteLine($"transaction: {session.GetProperty("transactionId")}");

// Send 16 kHz mono PCM16 chunks as they are captured.
await connection.SendAsync("SendAudio", pcm16Chunk);

var result = await connection.InvokeAsync<JsonElement>("StopMicSession");
// Stop returns ids only — long transcripts exceed SignalR message limits.
// Fetch the persisted transcript over REST; with an agent slug, keep polling
// the same GET until corrected_text appears (correction runs in the background).
var transactionId = result.GetProperty("transactionId").GetString();
Console.WriteLine($"saved transaction: {transactionId} — GET /v1/audio/transcriptions/{transactionId} for the text");`,

    curl: `# Realtime streaming requires a SignalR client; curl cannot drive the hub.
# For file-based realtime progress, create the job over REST with mode=realtime_file
# and subscribe to the hub with WatchTransaction(<transaction id>, null, null) —
# tenant/project may be null because your API key is tenant-scoped.

curl -X POST "${baseUrl}/v1/audio/transcriptions" \\
  -H "Authorization: Bearer <your project API key>" \\
  -F "file=@meeting.mp3" \\
  -F "mode=realtime_file" \\
  -F "language=en-US"`,
  };

  const agentExamples: Record<LanguageKey, string> = {
    python: `import time
import requests

BASE_URL = "${baseUrl}"
API_KEY = "<your project API key>"
AGENT_SLUG = "<agent-slug>"  # Agents -> your corrector agent -> slug

headers = {
    "X-Api-Key": API_KEY,  # or Authorization: Bearer <key>
    "X-Correlation-Id": "order-4711",
}

with open("meeting.mp3", "rb") as audio:
    response = requests.post(
        f"{BASE_URL}/api/v1/agents/{AGENT_SLUG}/transcriptions",
        headers=headers,
        files={"file": ("meeting.mp3", audio, "audio/mpeg")},
        data={"mode": "batch"},  # language/diarization/vocabulary come from the agent's Speech config
    )
response.raise_for_status()  # 404 = unknown agent slug
job = response.json()

# Poll until the transcript is ready.
while job["status"] in ("Pending", "Processing"):
    time.sleep(2)
    job = requests.get(
        f"{BASE_URL}/api/v1/agents/{AGENT_SLUG}/transcriptions/{job['id']}",
        headers={"X-Api-Key": API_KEY},
    ).json()

if job["status"] == "Completed":
    print("raw STT:      ", job["text"])
    # corrected_text is the agent-prompt-corrected transcript; null if correction failed.
    print("corrected:    ", job.get("corrected_text"))
else:
    print("failed:", job["error_code"], job["error_message"])`,

    csharp: `using System.Text.Json;

const string BaseUrl = "${baseUrl}";
const string ApiKey = "<your project API key>";
const string AgentSlug = "<agent-slug>"; // Agents -> your corrector agent -> slug

using var http = new HttpClient { BaseAddress = new Uri(BaseUrl) };
http.DefaultRequestHeaders.Add("X-Api-Key", ApiKey); // or Authorization: Bearer <key>
http.DefaultRequestHeaders.Add("X-Correlation-Id", "order-4711");

using var form = new MultipartFormDataContent();
await using var audio = File.OpenRead("meeting.mp3");
form.Add(new StreamContent(audio), "file", "meeting.mp3");
// language/diarization/vocabulary come from the agent's Speech config; form fields override per job.
form.Add(new StringContent("batch"), "mode");

var createResponse = await http.PostAsync($"/api/v1/agents/{AgentSlug}/transcriptions", form);
createResponse.EnsureSuccessStatusCode(); // 404 = unknown agent slug

var job = JsonSerializer.Deserialize<JsonElement>(await createResponse.Content.ReadAsStringAsync());
var transactionId = job.GetProperty("id").GetString();

// Poll until the transcript is ready.
string status;
do
{
    await Task.Delay(TimeSpan.FromSeconds(2));
    var pollJson = await http.GetStringAsync($"/api/v1/agents/{AgentSlug}/transcriptions/{transactionId}");
    job = JsonSerializer.Deserialize<JsonElement>(pollJson);
    status = job.GetProperty("status").GetString()!;
}
while (status is "Pending" or "Processing");

if (status == "Completed")
{
    Console.WriteLine($"raw STT:   {job.GetProperty("text").GetString()}");
    // corrected_text is the agent-prompt-corrected transcript; null if correction failed.
    Console.WriteLine($"corrected: {(job.TryGetProperty("corrected_text", out var c) ? c.GetString() : null)}");
}`,

    curl: `# Create the job — the agent's Speech config (language, diarization, phrase list) is applied,
# and after STT the agent's prompt corrects the transcript.
curl -X POST "${baseUrl}/api/v1/agents/<agent-slug>/transcriptions" \\
  -H "X-Api-Key: <your project API key>" \\
  -H "X-Correlation-Id: order-4711" \\
  -F "file=@meeting.mp3" \\
  -F "mode=batch"

# Poll for the transcript (404 if the slug is unknown or the job belongs to another agent).
# The completed response includes both "text" (raw STT) and "corrected_text" (agent-corrected).
curl "${baseUrl}/api/v1/agents/<agent-slug>/transcriptions/<transaction id>" \\
  -H "X-Api-Key: <your project API key>"`,
  };

  return (
    <div className="transcription-how-to-use" data-testid="transcription-howto-detail-page">
      <ShellHeader
        title="How to Use the Transcription APIs"
        description="Speech-to-text for third-party applications. Authenticate with a project API key; every job returns a correlation id you can quote when reporting a problem."
      />

      <PageContainer>
      <div className="mb-3 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
        The unscoped <code>/v1/audio/transcriptions</code> endpoints transcribe only — no synthesized audio,
        no agent replies. The agent-scoped <code>/api/v1/agents/{'{slug}'}/transcriptions</code> endpoints
        additionally run the agent's prompt to correct the transcript: the response carries both{' '}
        <code>text</code> (raw speech-to-text) and <code>corrected_text</code>.
      </div>

      <Section title="Endpoints">
        <div className="flex flex-col gap-1.5">
          {[
            ['POST', '/v1/audio/transcriptions', 'Upload audio and create a job. Modes: batch, batch_recording, realtime_file.'],
            ['GET', '/v1/audio/transcriptions/{id}', 'Poll a job; returns the transcript once status is Completed.'],
            ['GET', '/v1/audio/transcriptions?correlation_id={id}', 'List every job recorded under a correlation.'],
            ['HUB', '/hubs/transcription', 'Live mic: StartBrowserMicSession + ReportFinal (recognition in your app with a token from POST /v1/audio/speech-token) or StartMicSession + SendAudio (server-side recognition); StopMicSession ends either. WatchTransaction streams realtime-file progress.'],
          ].map(([method, path, description]) => (
            <div key={path} className="flex items-start gap-2">
              <Chip label={method ?? ''} color={method === 'POST' ? 'blue' : 'default'} className="text-xs min-w-[60px]" />
              <div>
                <p className="font-mono text-sm">{path}</p>
                <span className="text-xs text-[var(--color-text-secondary)]">{description}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Transcribe with an agent (speech config + prompt correction)">
        <p className="mb-2 text-sm text-[var(--color-text-secondary)]">
          Scope a job to an agent to apply that agent's <strong>Speech</strong> config (language, diarization,
          phrase list) during recognition, then run the agent's prompt to correct the transcript. Configure it
          on <strong>Agents → Edit → Speech</strong>. Speech credentials stay on the server
          (<code>AzureSpeech:*</code>) — the agent never stores keys. Request form fields
          (<code>language</code>, <code>diarization</code>, <code>vocabulary</code>) override the agent
          defaults for that job only.
        </p>
        <div className="mb-2 flex flex-col gap-1.5">
          {[
            ['POST', '/api/v1/agents/{slug}/transcriptions', 'Upload audio; uses the agent\u2019s Speech config, then runs the agent prompt to correct the transcript. Modes: batch, batch_recording, realtime_file.'],
            ['GET', '/api/v1/agents/{slug}/transcriptions/{id}', 'Poll; text is raw STT, corrected_text is the agent-corrected output when correction succeeded. 404 if the slug is unknown or the job is not for that agent.'],
            ['GET', '/api/v1/agents/{slug}/transcriptions?correlation_id={id}', 'List jobs for that agent recorded under a correlation.'],
            ['POST|GET', '/v1/agents/{slug}/transcriptions', 'Same controller without the /api prefix.'],
            ['HUB', 'StartMicSession(language, clientCorrelationId, tenantId, projectId, agentSlug)', 'Optional last argument applies the agent\u2019s Speech config and runs correction when the session stops.'],
          ].map(([method, path, description]) => (
            <div key={path} className="flex items-start gap-2">
              <Chip
                label={method ?? ''}
                color={method?.startsWith('POST') ? 'blue' : 'default'}
                className="text-xs min-w-[60px]"
              />
              <div>
                <p className="font-mono text-sm">{path}</p>
                <span className="text-xs text-[var(--color-text-secondary)]">{description}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Authenticate with a project API key via <code>Authorization: Bearer &lt;key&gt;</code> or{' '}
          <code>X-Api-Key: &lt;key&gt;</code> — the same options as the other public agent APIs.
        </p>
      </Section>

      <Section title="Payload reference">
        <p className="mb-2 text-sm text-[var(--color-text-secondary)]">
          Creating a job is a file upload, not a JSON body. You send named form fields plus the audio.
          The API answers with JSON using <strong>snake_case</strong> names (<code>correlation_id</code>).
          Fields that are empty are left out of the JSON. Live microphone events on the hub use{' '}
          <strong>camelCase</strong> (<code>transactionId</code>).
        </p>
        <div className="mb-2 grid grid-cols-1 gap-1.5 md:grid-cols-3">
          {[
            ['1. Upload', 'POST the audio file. You receive id and correlation_id immediately (HTTP 202).'],
            ['2. Wait', 'Poll GET until status is no longer Pending or Processing.'],
            ['3. Read', 'On Completed, use text. On an agent job, also read corrected_text when it is present.'],
          ].map(([step, detail]) => (
            <div
              key={step}
              className="rounded border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-1.5"
            >
              <span className="mb-0.5 block text-sm font-semibold">{step}</span>
              <span className="text-xs text-[var(--color-text-secondary)]">{detail}</span>
            </div>
          ))}
        </div>
        <Tabs
          value={payloadTab}
          onChange={(_, value: PayloadTab) => setPayloadTab(value)}
          aria-label="Payload reference"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab value="request" label="Request" data-testid="transcription-howto-tab-request" />
          <Tab value="response" label="Response" data-testid="transcription-howto-tab-response" />
          <Tab value="hub" label="Live hub" data-testid="transcription-howto-tab-hub" />
        </Tabs>

        {payloadTab === 'request' && (
          <div>
            <span className="mb-1 block text-sm font-semibold">Headers</span>
            <FieldTable fields={REQUEST_HEADERS} fieldLabel="Header" />
            <span className="mb-1 block text-sm font-semibold">Form fields</span>
            <p className="mb-1.5 text-sm text-[var(--color-text-secondary)]">
              Send these as multipart parts alongside <code>file</code>. On{' '}
              <code>/api/v1/agents/{'{slug}'}/transcriptions</code>, <code>language</code>,{' '}
              <code>diarization</code>, and <code>vocabulary</code> override the agent Speech config for
              that job only. Max speakers and phrase-list weight always come from the agent.
            </p>
            <FieldTable fields={REQUEST_FIELDS} />
            <span className="mb-1 block text-sm font-semibold">What you send</span>
            <CodeBlock code={REQUEST_SHAPE} onCopy={copy} />
          </div>
        )}

        {payloadTab === 'response' && (
          <div>
            <p className="mb-1.5 text-sm text-[var(--color-text-secondary)]">
              Create returns HTTP 202 with the job object (<code>status</code> is usually{' '}
              <code>Pending</code>). Poll the same object until it is terminal. List-by-correlation wraps
              jobs in <code>data</code>. Validation errors use a <code>message</code> field (camelCase).
            </p>
            <FieldTable fields={RESPONSE_FIELDS} />
            <span className="mb-1 block text-sm font-semibold">Accepted job (HTTP 202)</span>
            <CodeBlock code={ACCEPTED_EXAMPLE} onCopy={copy} />
            <span className="mb-1 mt-3 block text-sm font-semibold">Completed job (HTTP 200) — includes agent fields</span>
            <CodeBlock code={COMPLETED_EXAMPLE} onCopy={copy} />
            <span className="mb-1 mt-3 block text-sm font-semibold">List by correlation</span>
            <CodeBlock code={LIST_EXAMPLE} onCopy={copy} />
            <span className="mb-1 mt-3 block text-sm font-semibold">Error body (HTTP 400 / 404)</span>
            <CodeBlock code={ERROR_EXAMPLE} onCopy={copy} />
          </div>
        )}

        {payloadTab === 'hub' && (
          <div>
            <p className="mb-1.5 text-sm text-[var(--color-text-secondary)]">
              Connect to <code>/hubs/transcription</code> with the same API key as the REST calls.
              Property names on this hub are camelCase. Two live-mic styles exist: browser-side
              recognition (<code>StartBrowserMicSession</code> + <code>ReportFinal</code>, using the
              Azure Speech JS SDK with a short-lived token from{' '}
              <code>POST /v1/audio/speech-token</code>) and server-side push-stream
              (<code>StartMicSession</code> + <code>SendAudio</code>, 16 kHz mono PCM16). The hub
              returns transcript text only — never synthesized speech.
            </p>
            <span className="mb-1 block text-sm font-semibold">Methods you call</span>
            <FieldTable fields={HUB_METHODS} fieldLabel="Method" />
            <span className="mb-1 block text-sm font-semibold">Events you receive</span>
            <FieldTable fields={HUB_EVENTS} fieldLabel="Event" />
          </div>
        )}
      </Section>

      <div className="mb-3 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)]">
        <Tabs value={language} onChange={(_, value: LanguageKey) => setLanguage(value)} aria-label="Code examples">
          <Tab value="python" label="Python" data-testid="transcription-howto-tab-python" />
          <Tab value="csharp" label="C#" data-testid="transcription-howto-tab-csharp" />
          <Tab value="curl" label="curl" data-testid="transcription-howto-tab-curl" />
        </Tabs>
        <hr className="border-[var(--color-border-default)]" />
        <div className="p-3">
          <span className="mb-1 block text-sm font-semibold">
            Batch: upload a file and poll for the transcript
          </span>
          <CodeBlock code={batchExamples[language]} onCopy={copy} />

          <span className="mb-1 mt-4 block text-sm font-semibold">
            Agent-scoped batch: agent Speech config + prompt-corrected transcript
          </span>
          <CodeBlock code={agentExamples[language]} onCopy={copy} />

          <span className="mb-1 mt-4 block text-sm font-semibold">
            Realtime: stream the microphone and transcribe as it happens
          </span>
          <CodeBlock code={realtimeExamples[language]} onCopy={copy} />
        </div>
      </div>

      <Section title="Troubleshooting with correlation ids">
        <p className="mb-1.5 text-sm text-[var(--color-text-secondary)]">
          Send an <code>X-Correlation-Id</code> header with your own trace value. Every job, status change, and
          error is recorded against both your value and the correlation id we return, so support can find the
          exact run from either side.
        </p>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Open <strong>Voice &amp; Transcription → Transcriptions</strong> and search by either identifier — or
          by agent slug — to see the request metadata, transcript, and full event timeline for a job. For
          agent-scoped jobs, the timeline includes <code>corrected</code> when prompt correction succeeded or{' '}
          <code>correction_failed</code> when it did not (the raw transcript is still saved).
        </p>
      </Section>
      </PageContainer>
    </div>
  );
};
